import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { deferredImports } from '../../utils/tbtOptimization';
import { logger } from '../../utils/logger';

interface LottieProps {
  animationData: object;
  className?: string;
  style?: React.CSSProperties;
  loop?: boolean;
  autoplay?: boolean;
  width?: number | string;
  height?: number | string;
  onComplete?: () => void;
  rendererSettings?: {
    preserveAspectRatio?: string;
    clearCanvas?: boolean;
    progressiveLoad?: boolean;
    hideOnTransparent?: boolean;
    [key: string]: unknown;
  };
  [key: string]: unknown; // Allow additional props
}

interface OptimizedLottieProps {
  animationData: object;
  className?: string;
  style?: React.CSSProperties;
  loop?: boolean;
  autoplay?: boolean;
  width?: number | string;
  height?: number | string;
  lazy?: boolean; // Enable intersection observer
  threshold?: number; // Intersection threshold
  rootMargin?: string; // Intersection root margin
  onAnimationComplete?: () => void;
  onAnimationStart?: () => void;
  priority?: 'high' | 'low'; // Loading priority
}

const OptimizedLottie: React.FC<OptimizedLottieProps> = ({
  animationData,
  className,
  style,
  loop = true,
  autoplay = true,
  width,
  height,
  lazy = true,
  threshold = 0.1,
  rootMargin = '50px',
  onAnimationComplete,
  onAnimationStart,
  priority = 'low',
  ...props
}) => {
  const [LottieComponent, setLottieComponent] = useState<React.ComponentType<LottieProps> | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(true);
  const [isVisible, setIsVisible] = useState(!lazy);
  const [shouldLoad, setShouldLoad] = useState(!lazy || priority === 'high');
  const containerRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  // Memoize animation data to prevent unnecessary re-renders
  const memoizedAnimationData = useMemo(() => animationData, [animationData]);

  // Intersection Observer setup
  useEffect(() => {
    if (!lazy || shouldLoad) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            setShouldLoad(true);
            observer.disconnect();
          }
        });
      },
      {
        threshold,
        rootMargin,
      },
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    observerRef.current = observer;

    return () => {
      observer.disconnect();
    };
  }, [lazy, shouldLoad, threshold, rootMargin]);

  // Load Lottie library
  const loadLottie = useCallback(async (): Promise<void> => {
    if (LottieComponent || !shouldLoad) return;

    try {
      setIsLoading(true);

      // Add delay for low priority animations to improve TBT
      if (priority === 'low') {
        await new Promise((resolve) => setTimeout(resolve, 100));
      }

      const lottieModule = (await deferredImports.lottie()) as typeof import('lottie-react');
      setLottieComponent(() => lottieModule.default);

      if (onAnimationStart) {
        onAnimationStart();
      }
    } catch (error) {
      logger.error('Failed to load Lottie:', error);
    } finally {
      setIsLoading(false);
    }
  }, [LottieComponent, shouldLoad, priority, onAnimationStart]);

  useEffect(() => {
    if (shouldLoad) {
      loadLottie();
    }
  }, [shouldLoad, loadLottie]);

  // Handle animation complete
  const handleComplete = useCallback(() => {
    if (onAnimationComplete) {
      onAnimationComplete();
    }
  }, [onAnimationComplete]);

  // Render loading placeholder
  const renderPlaceholder = () => (
    <div
      ref={containerRef}
      className={`${className} bg-gray-100 animate-pulse rounded-lg flex items-center justify-center transition-all duration-300`}
      style={{
        width,
        height,
        minHeight: typeof height === 'number' ? `${height}px` : height || '200px',
        minWidth: typeof width === 'number' ? `${width}px` : width || '200px',
        ...style,
      }}
      aria-label="Loading animation"
    >
      <div className="text-gray-400 text-sm font-medium">
        {isLoading ? 'Loading animation...' : 'Animation ready'}
      </div>
    </div>
  );

  // Don't load if not visible and lazy loading is enabled
  if (lazy && !isVisible) {
    return renderPlaceholder();
  }

  // Show loading state
  if (isLoading || !LottieComponent) {
    return renderPlaceholder();
  }

  // Render the actual Lottie animation
  return (
    <div ref={containerRef} className="lottie-container">
      <LottieComponent
        animationData={memoizedAnimationData}
        className={className}
        style={{
          width,
          height,
          ...style,
        }}
        loop={loop}
        autoplay={autoplay && isVisible}
        onComplete={handleComplete}
        rendererSettings={{
          preserveAspectRatio: 'xMidYMid slice',
          clearCanvas: true,
          progressiveLoad: true,
          hideOnTransparent: true,
        }}
        {...props}
      />
    </div>
  );
};

export default React.memo(OptimizedLottie);
